// Jobs API — Cloudflare Workers API (same backend as mobile app)
// Handles: video generation job creation, listing, deletion
// Job creation triggers the VideoGenerationWorkflow on Cloudflare
import { apiClient } from '../client.js';

export const jobsApi = {
  /**
   * Create a new video generation job.
   * This triggers the Cloudflare VideoGenerationWorkflow:
   * 1. Validates draft & credits
   * 2. Reserves credits
   * 3. Starts workflow (OpenAI LLM → Sora → persist outputs)
   *
   * @param {{ draftId: string, idempotencyKey: string, costCredits?: number }} request
   * @returns {Promise<{ job: { id, status, costCredits, expiresAt } }>}
   */
  async create(request) {
    return apiClient.post('/jobs', {
      draftId: request.draftId,
      idempotencyKey: request.idempotencyKey,
      costCredits: request.costCredits || 1,
    });
  },

  /**
   * List user's jobs with cursor-based pagination.
   * @param {{ cursor?: string, limit?: number }} [params]
   * @returns {Promise<{ items: Array<{ id, status, statusReason, title, thumbnailUrl, videoUrl, createdAt, updatedAt, expiresAt }>, nextCursor: string|null }>}
   */
  async list(params = {}) {
    const searchParams = new URLSearchParams();
    if (params.cursor) searchParams.set('cursor', params.cursor);
    if (params.limit) searchParams.set('limit', params.limit.toString());
    const query = searchParams.toString();
    return apiClient.get(`/jobs${query ? '?' + query : ''}`);
  },

  /**
   * Get a specific job by ID.
   * @param {string} id
   * @returns {Promise<{ job: { id, status, statusReason, title, costCredits, videoUrl, thumbnailUrl, expiresAt, createdAt } }>}
   */
  async getById(id) {
    return apiClient.get(`/jobs/${id}`);
  },

  /**
   * Delete a job (also deletes R2 video/thumbnail files).
   * @param {string} id
   * @returns {Promise<{ success: boolean }>}
   */
  async delete(id) {
    return apiClient.post(`/jobs/${id}/delete`);
  },

  /**
   * Mark a stuck job as failed and refund credits.
   * Used when workflow is terminated or timed out.
   * @param {string} id
   * @returns {Promise<{ success: boolean, refunded: boolean, message: string }>}
   */
  async failStuck(id) {
    return apiClient.post(`/jobs/${id}/fail-stuck`);
  },
};

/**
 * Normalize Worker API job response → UI-friendly shape.
 * Worker returns camelCase, but we add aliases for UI components.
 * @param {Object} item - Job item from Worker API
 * @returns {Object} Normalized job for UI components
 */
export function normalizeJob(item) {
  if (!item) return null;
  return {
    id: item.id,
    status: item.status,
    statusReason: item.statusReason,
    costCredits: item.costCredits,
    videoUrl: item.videoUrl,
    url: item.videoUrl,              // alias — player/modal components use video.url
    thumbnail: item.thumbnailUrl,    // alias — VideoCard uses video.thumbnail
    thumbnailUrl: item.thumbnailUrl,
    title: (typeof item.title === 'string' && item.title.trim()) ? item.title.trim() : 'Video',
    expiresAt: item.expiresAt,
    createdAt: item.createdAt,
    updatedAt: item.updatedAt,
  };
}
